import { motion } from 'motion/react';
import { Card } from './ui/card';
import { Wind, ThumbsUp, AlertCircle, AlertTriangle } from 'lucide-react';

interface AirQualityIndicatorProps {
  temperature: number;
  humidity: number;
  pressure: number;
}

type QualityLevel = {
  level: string;
  color: string;
  bgColor: string;
  icon: any;
  description: string;
};

function calculateAirQuality(temp: number, humidity: number, pressure: number): QualityLevel {
  // Simplified air quality calculation based on comfort parameters
  let score = 100;
  
  // Temperature penalty (ideal 18-24°C)
  if (temp < 18 || temp > 24) {
    score -= Math.abs(temp - 21) * 3;
  }
  
  // Humidity penalty (ideal 40-60%)
  if (humidity < 40 || humidity > 60) {
    score -= Math.abs(humidity - 50) * 2;
  }
  
  // Pressure penalty (ideal 1010-1020 hPa)
  if (pressure < 1010 || pressure > 1020) {
    score -= Math.abs(pressure - 1015) * 2;
  }
  
  score = Math.max(0, Math.min(100, score));
  
  if (score >= 80) {
    return {
      level: 'Doskonała',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      icon: ThumbsUp,
      description: 'Warunki idealne do aktywności na świeżym powietrzu',
    };
  } else if (score >= 60) {
    return {
      level: 'Dobra',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      icon: Wind,
      description: 'Warunki dobre, komfortowa pogoda',
    };
  } else if (score >= 40) {
    return {
      level: 'Umiarkowana',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      icon: AlertCircle,
      description: 'Warunki przeciętne, możliwy dyskomfort',
    };
  } else {
    return {
      level: 'Słaba',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      icon: AlertTriangle,
      description: 'Niekorzystne warunki atmosferyczne',
    };
  }
}

export function AirQualityIndicator({ temperature, humidity, pressure }: AirQualityIndicatorProps) {
  const quality = calculateAirQuality(temperature, humidity, pressure);
  const Icon = quality.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.15 }}
    >
      <Card className={`p-6 ${quality.bgColor} border-2`}>
        <div className="flex items-start gap-4">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }}
          >
            <Icon className={`w-12 h-12 ${quality.color}`} />
          </motion.div>
          
          <div className="flex-1">
            <h3 className={`mb-1 ${quality.color}`}>
              Komfort pogodowy
            </h3>
            <p className={`text-2xl mb-2 ${quality.color}`}>
              {quality.level}
            </p>
            <p className="text-sm text-gray-600">
              {quality.description}
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
